import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateSchema, isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { attachmentSchema, createLinkAttachmentRequestSchema } from '../governance/topics.js';
/**
 * Management finance (spec §7.14, §7.17, §7.19, ADR-0007). Every amount travels as a decimal string
 * with its ISO currency; the API never sends binary floating point. Figures are computed by the
 * backend from the immutable journal; the UI only formats them.
 */
export const currencyCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Enter a 3-letter ISO currency code');
/** Positive amount with at most 16 integer digits and 4 decimals (`numeric(20,4)`). */
export const positiveAmountSchema = z
    .string()
    .trim()
    .regex(/^\d{1,16}(\.\d{1,4})?$/, 'Enter an amount such as 1250.50, with at most 4 decimals')
    .refine((value) => /[1-9]/.test(value), { message: 'The amount must be greater than zero' });
/** Signed amount (balances may be negative, e.g. an overdraft). */
export const signedAmountSchema = z
    .string()
    .trim()
    .regex(/^-?\d{1,16}(\.\d{1,4})?$/, 'Enter an amount such as 1250.50, with at most 4 decimals');
/** FX rate: units of the quote currency per one unit of the base currency (`numeric(20,10)`). */
export const fxRateValueSchema = z
    .string()
    .trim()
    .regex(/^\d{1,10}(\.\d{1,10})?$/, 'Enter a rate with at most 10 decimals')
    .refine((value) => /[1-9]/.test(value), { message: 'The rate must be greater than zero' });
/** Calendar month `YYYY-MM`. */
export const isoMonthSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Enter a month as YYYY-MM');
export const moneySchema = z.object({ amount: z.string(), currency: z.string() });
// --- reference data -----------------------------------------------------------------------------
export const FINANCE_CATEGORY_KINDS = [
    'INCOME',
    'EXPENSE',
    'TRANSFER',
    'EQUITY',
    'TAX',
    'OTHER',
];
export const financeCategoryKindSchema = z.enum(FINANCE_CATEGORY_KINDS);
/** Category kinds a transaction of each kind may use (taxes paid are a management expense). */
export const CATEGORY_KINDS_FOR_TRANSACTION = {
    INCOME: ['INCOME'],
    EXPENSE: ['EXPENSE', 'TAX'],
};
export const financeCategorySchema = z.object({
    id: uuidSchema,
    kind: financeCategoryKindSchema,
    code: z.string(),
    name: z.string(),
    parentId: uuidSchema.nullable(),
    archived: z.boolean(),
});
export const financeCategoryListSchema = z.object({ items: z.array(financeCategorySchema) });
export const createFinanceCategoryRequestSchema = z.object({
    kind: financeCategoryKindSchema,
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9-]{1,31}$/, 'Use 2–32 letters, digits or dashes'),
    name: z.string().trim().min(2).max(100),
    parentId: uuidSchema.nullable().default(null),
});
export const updateFinanceCategoryRequestSchema = z.object({
    name: z.string().trim().min(2).max(100),
    archived: z.boolean(),
    reason: reasonSchema,
});
export const DATA_SOURCE_KINDS = [
    'ACCOUNTING_SYSTEM',
    'BANK',
    'PAYMENT_PROVIDER',
    'BILLING',
    'CRM',
    'MANUAL_ENTRY',
];
export const dataSourceKindSchema = z.enum(DATA_SOURCE_KINDS);
export const financeDataSourceSchema = z.object({
    id: uuidSchema,
    kind: dataSourceKindSchema,
    name: z.string(),
    /** Spec §7.19: lower number = preferred source. */
    priority: z.number().int(),
    status: z.enum(['ACTIVE', 'DISABLED']),
    requiresEvidence: z.boolean(),
    /** Empty until the founders decide which source is authoritative per datum (open decision 14). */
    authoritativeFor: z.array(z.string()),
    /** Integration adapter state; `null` for manual entry. */
    adapter: z
        .object({
        key: z.string(),
        configured: z.boolean(),
        note: z.string(),
    })
        .nullable(),
});
export const financeDataSourceListSchema = z.object({ items: z.array(financeDataSourceSchema) });
// --- settings -----------------------------------------------------------------------------------
/**
 * Workspace finance policy. None of these values rewrites history: consolidated figures are
 * recomputed from original amounts and the historical FX rates on every read.
 */
export const financePolicySchema = z.object({
    /** Consolidated reporting currency; `null` = show each currency separately (not decided yet). */
    reportingCurrency: currencyCodeSchema.nullable(),
    /** Complete months averaged for monthly net burn (spec §7.14 "configurable calculation window"). */
    burnWindowMonths: z.number().int().min(1).max(12),
    /** First month of the fiscal year for YTD figures (1 = January). */
    fiscalYearStartMonth: z.number().int().min(1).max(12),
});
/** Engineering defaults until the founders save their own (ADR-0007). */
export const DEFAULT_FINANCE_POLICY = {
    reportingCurrency: null,
    burnWindowMonths: 3,
    fiscalYearStartMonth: 1,
};
export const financeSettingsSchema = financePolicySchema.extend({
    isDefault: z.boolean(),
    version: z.number().int().min(0),
});
export const updateFinanceSettingsRequestSchema = financePolicySchema.extend({
    version: z.number().int().min(0),
    reason: reasonSchema,
});
// --- bank accounts, balances and FX -------------------------------------------------------------
export const CASH_ACCOUNT_KINDS = ['BANK', 'CASH'];
export const cashAccountKindSchema = z.enum(CASH_ACCOUNT_KINDS);
export const balanceSnapshotSchema = z.object({
    id: uuidSchema,
    asOf: isoDateTimeSchema,
    balance: moneySchema,
    dataSource: z.string(),
    recordedBy: z.string().nullable(),
    evidence: z.object({ url: z.string(), title: z.string() }).nullable(),
    createdAt: isoDateTimeSchema,
});
export const bankAccountSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    kind: cashAccountKindSchema,
    institutionName: z.string().nullable(),
    accountMask: z.string().nullable(),
    currency: z.string(),
    isOperating: z.boolean(),
    status: z.enum(['ACTIVE', 'CLOSED']),
    dataSource: z.string().nullable(),
    ledgerAccountCode: z.string(),
    /** Balance computed from the journal (book cash). */
    bookBalance: moneySchema,
    /** Latest observed balance reported by the bank/statement, with provenance. */
    latestSnapshot: balanceSnapshotSchema.nullable(),
    version: versionSchema,
});
export const bankAccountListSchema = z.object({ items: z.array(bankAccountSchema) });
export const createBankAccountRequestSchema = z
    .object({
    name: z.string().trim().min(2).max(150),
    kind: cashAccountKindSchema,
    institutionName: z.string().trim().max(150).nullable().default(null),
    /** Last digits only; full account numbers are never stored. */
    accountMask: z
        .string()
        .trim()
        .regex(/^[0-9]{2,4}$/, 'Enter the last 2–4 digits only')
        .nullable()
        .default(null),
    currency: currencyCodeSchema,
    isOperating: z.boolean(),
    /** Book balance already on the account when it is added (posted as an opening entry). */
    openingBalance: signedAmountSchema.nullable().default(null),
    openingDate: isoDateSchema.nullable().default(null),
    openingEvidence: createLinkAttachmentRequestSchema.nullable().default(null),
    reason: reasonSchema,
})
    .refine((value) => value.openingBalance === null || value.openingDate !== null, {
    message: 'Choose the date of the opening balance',
    path: ['openingDate'],
});
export const updateBankAccountRequestSchema = z.object({
    version: versionSchema,
    name: z.string().trim().min(2).max(150),
    isOperating: z.boolean(),
    /** Closing requires a zero book balance; closed accounts accept no new entries. */
    status: z.enum(['ACTIVE', 'CLOSED']),
    reason: reasonSchema,
});
export const recordBalanceSnapshotRequestSchema = z.object({
    asOf: isoDateTimeSchema,
    balance: signedAmountSchema,
    /** Where the figure comes from, e.g. a statement link; required for manual entry. */
    evidence: createLinkAttachmentRequestSchema.nullable().default(null),
});
export const fxRateSchema = z.object({
    id: uuidSchema,
    baseCurrency: z.string(),
    quoteCurrency: z.string(),
    rate: z.string(),
    rateDate: isoDateSchema,
    source: z.string(),
    createdAt: isoDateTimeSchema,
});
export const fxRatePageSchema = cursorPageSchema(fxRateSchema);
export const fxRateQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    currency: currencyCodeSchema.optional(),
});
export const createFxRateRequestSchema = z
    .object({
    baseCurrency: currencyCodeSchema,
    quoteCurrency: currencyCodeSchema,
    rate: fxRateValueSchema,
    rateDate: isoDateSchema,
    /** Publisher of the rate, e.g. "Central Bank of Armenia". */
    source: z.string().trim().min(2).max(100),
})
    .refine((value) => value.baseCurrency !== value.quoteCurrency, {
    message: 'Choose two different currencies',
    path: ['quoteCurrency'],
});
/** The historical rate used to convert one currency into the reporting currency. */
export const appliedRateSchema = z.object({
    fromCurrency: z.string(),
    toCurrency: z.string(),
    /** Rate actually applied (quote per base of the stored pair). */
    rate: z.string(),
    /** True when the stored pair is the inverse and the amount was divided by the rate. */
    inverted: z.boolean(),
    rateDate: isoDateSchema,
    source: z.string(),
});
export const missingRateSchema = z.object({ currency: z.string(), onDate: isoDateSchema });
// --- financial transactions ---------------------------------------------------------------------
export const FINANCIAL_TRANSACTION_KINDS = ['INCOME', 'EXPENSE'];
export const financialTransactionKindSchema = z.enum(FINANCIAL_TRANSACTION_KINDS);
export const FINANCIAL_TRANSACTION_STATUSES = ['DRAFT', 'RECORDED', 'VOIDED'];
export const financialTransactionStatusSchema = z.enum(FINANCIAL_TRANSACTION_STATUSES);
export const PAYER_TYPES = ['COMPANY', 'FOUNDER'];
export const payerTypeSchema = z.enum(PAYER_TYPES);
export const RECONCILIATION_STATUSES = [
    'UNRECONCILED',
    'MATCHED',
    'RECONCILED',
    'DISPUTED',
    'EXCLUDED',
];
export const reconciliationStatusSchema = z.enum(RECONCILIATION_STATUSES);
export const APPROVAL_STATUSES = ['NOT_REQUIRED', 'PENDING', 'APPROVED', 'REJECTED'];
export const approvalStatusSchema = z.enum(APPROVAL_STATUSES);
const nameRef = z.object({ id: uuidSchema, name: z.string() });
export const financialTransactionSummarySchema = z.object({
    id: uuidSchema,
    kind: financialTransactionKindSchema,
    status: financialTransactionStatusSchema,
    transactionDate: isoDateSchema,
    amount: moneySchema,
    category: z.object({ id: uuidSchema, code: z.string(), name: z.string() }).nullable(),
    counterpartyName: z.string().nullable(),
    description: z.string().nullable(),
    payerType: payerTypeSchema,
    payerFounder: nameRef.nullable(),
    bankAccount: nameRef.nullable(),
    dataSource: z.object({ id: uuidSchema, name: z.string(), kind: dataSourceKindSchema }),
    reconciliationStatus: reconciliationStatusSchema,
    approvalStatus: approvalStatusSchema,
    evidenceCount: z.number().int(),
    replacesTransactionId: uuidSchema.nullable(),
    replacedByTransactionId: uuidSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const financialTransactionPageSchema = cursorPageSchema(financialTransactionSummarySchema);
export const journalLineViewSchema = z.object({
    lineNumber: z.number().int(),
    accountCode: z.string(),
    accountName: z.string(),
    direction: z.enum(['DEBIT', 'CREDIT']),
    amount: moneySchema,
    category: z.string().nullable(),
    founderName: z.string().nullable(),
    memo: z.string().nullable(),
});
export const journalEntryViewSchema = z.object({
    id: uuidSchema,
    entryNumber: z.number().int(),
    entryDate: isoDateSchema,
    description: z.string(),
    sourceType: z.string(),
    reason: z.string().nullable(),
    postedBy: z.string().nullable(),
    postedAt: isoDateTimeSchema,
    lines: z.array(journalLineViewSchema),
});
export const financialTransactionDetailSchema = financialTransactionSummarySchema.extend({
    invoiceRef: z.string().nullable(),
    paymentRef: z.string().nullable(),
    paymentMethod: z.string().nullable(),
    taxTreatment: z.string().nullable(),
    externalRef: z.string().nullable(),
    importBatchId: uuidSchema.nullable(),
    createdBy: z.string().nullable(),
    voidReason: z.string().nullable(),
    voidedAt: isoDateTimeSchema.nullable(),
    voidedBy: z.string().nullable(),
    evidence: z.array(attachmentSchema),
    /** Posting and (after a void) its reversal — the deterministic record behind every figure. */
    journalEntries: z.array(journalEntryViewSchema),
    matchedBankMovements: z.array(z.object({
        id: uuidSchema,
        bookedOn: isoDateSchema,
        amount: moneySchema,
        description: z.string().nullable(),
    })),
    permissions: z.object({
        canEdit: z.boolean(),
        canRecord: z.boolean(),
        canVoid: z.boolean(),
        canCorrect: z.boolean(),
        canAddEvidence: z.boolean(),
    }),
    /** Evidence is required before recording (data source policy, spec §26). */
    evidenceRequired: z.boolean(),
    version: versionSchema,
});
const optionalText = (max) => z.string().trim().max(max).nullable().default(null);
const transactionFields = {
    transactionDate: isoDateSchema,
    amount: positiveAmountSchema,
    currency: currencyCodeSchema,
    categoryId: uuidSchema,
    counterpartyName: optionalText(200),
    invoiceRef: optionalText(100),
    paymentRef: optionalText(100),
    paymentMethod: optionalText(50),
    payerType: payerTypeSchema,
    payerFounderProfileId: uuidSchema.nullable().default(null),
    bankAccountId: uuidSchema.nullable().default(null),
    taxTreatment: optionalText(100),
    description: optionalText(2000),
};
function payerIsConsistent(value, ctx) {
    if (value.payerType === 'FOUNDER') {
        if (value.payerFounderProfileId === null) {
            ctx.addIssue({
                code: 'custom',
                message: 'Choose the founder who paid',
                path: ['payerFounderProfileId'],
            });
        }
        if (value.bankAccountId !== null) {
            ctx.addIssue({
                code: 'custom',
                message: 'A founder-paid expense does not move company cash',
                path: ['bankAccountId'],
            });
        }
    }
    else {
        if (value.bankAccountId === null) {
            ctx.addIssue({
                code: 'custom',
                message: 'Choose the company account',
                path: ['bankAccountId'],
            });
        }
        if (value.payerFounderProfileId !== null) {
            ctx.addIssue({
                code: 'custom',
                message: 'Only founder-paid expenses name a founder',
                path: ['payerFounderProfileId'],
            });
        }
    }
}
export const createFinancialTransactionRequestSchema = z
    .object({
    kind: financialTransactionKindSchema,
    ...transactionFields,
    evidence: z.array(createLinkAttachmentRequestSchema).max(10).default([]),
    /** RECORDED posts to the journal immediately; DRAFT can be edited and recorded later. */
    saveAs: z.enum(['DRAFT', 'RECORDED']).default('RECORDED'),
})
    .superRefine((value, ctx) => {
    payerIsConsistent(value, ctx);
    if (value.kind === 'INCOME' && value.payerType === 'FOUNDER') {
        ctx.addIssue({
            code: 'custom',
            message: 'Income is always received by the company',
            path: ['payerType'],
        });
    }
});
export const updateDraftTransactionRequestSchema = z
    .object({ ...transactionFields, version: versionSchema })
    .superRefine(payerIsConsistent);
export const recordTransactionRequestSchema = z.object({ version: versionSchema });
export const voidTransactionRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
/** Void + replacement in one step; the kind stays the same (spec §7.14 "reclassification"). */
export const correctTransactionRequestSchema = z
    .object({
    version: versionSchema,
    reason: reasonSchema,
    ...transactionFields,
    evidence: z.array(createLinkAttachmentRequestSchema).max(10).default([]),
})
    .superRefine(payerIsConsistent);
const csvList = (item, message) => z
    .string()
    .optional()
    .transform((value, ctx) => {
    if (value === undefined || value === '')
        return undefined;
    const parsed = z.array(item).safeParse(value.split(','));
    if (!parsed.success) {
        ctx.addIssue({ code: 'custom', message });
        return z.NEVER;
    }
    return parsed.data;
});
export const financialTransactionQuerySchema = z
    .object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    kind: financialTransactionKindSchema.optional(),
    status: csvList(financialTransactionStatusSchema, 'Unknown transaction status'),
    reconciliation: csvList(reconciliationStatusSchema, 'Unknown reconciliation status'),
    payer: payerTypeSchema.optional(),
    categoryId: uuidSchema.optional(),
    bankAccountId: uuidSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
})
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, {
    message: 'The range must end on or after its start',
    path: ['to'],
});
// --- reconciliation -----------------------------------------------------------------------------
export const BANK_RECONCILIATION_STATUSES = [
    'UNRECONCILED',
    'MATCHED',
    'RECONCILED',
    'SUSPECTED_DUPLICATE',
    'EXCLUDED',
];
export const bankReconciliationStatusSchema = z.enum(BANK_RECONCILIATION_STATUSES);
export const bankMovementSchema = z.object({
    id: uuidSchema,
    bankAccount: nameRef,
    externalId: z.string(),
    bookedOn: isoDateSchema,
    direction: z.enum(['INFLOW', 'OUTFLOW']),
    amount: moneySchema,
    description: z.string().nullable(),
    counterpartyName: z.string().nullable(),
    dataSource: z.string(),
    importBatchId: uuidSchema.nullable(),
    /** What the import was labelled with, e.g. the statement file name. */
    importLabel: z.string().nullable(),
    reconciliationStatus: bankReconciliationStatusSchema,
    matchedTransactionId: uuidSchema.nullable(),
    duplicateOfId: uuidSchema.nullable(),
    exclusionReason: z.string().nullable(),
    reconciledBy: z.string().nullable(),
    reconciledAt: isoDateTimeSchema.nullable(),
    version: versionSchema,
});
export const bankMovementPageSchema = cursorPageSchema(bankMovementSchema);
export const bankMovementQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    status: csvList(bankReconciliationStatusSchema, 'Unknown reconciliation status'),
    bankAccountId: uuidSchema.optional(),
});
/** One statement line, already normalised by the client (CSV) or an integration adapter. */
export const statementRowSchema = z.object({
    /** Bank reference when available; otherwise a fingerprint of the row is used. */
    externalId: z.string().trim().min(1).max(150).nullable().default(null),
    bookedOn: isoDateSchema,
    direction: z.enum(['INFLOW', 'OUTFLOW']),
    amount: positiveAmountSchema,
    description: z.string().trim().max(500).nullable().default(null),
    counterpartyName: z.string().trim().max(200).nullable().default(null),
});
export const importStatementRequestSchema = z.object({
    rows: z.array(statementRowSchema).min(1).max(1000),
    /** Statement file name or link, kept as provenance on the import batch. */
    sourceLabel: z.string().trim().min(1).max(200),
});
export const importResultSchema = z.object({
    importBatchId: uuidSchema,
    status: z.enum(['SUCCEEDED', 'PARTIAL', 'FAILED']),
    received: z.number().int(),
    created: z.number().int(),
    /** Already imported (same source identifier): skipped, never posted twice. */
    skippedDuplicates: z.number().int(),
    /** New rows that look like an existing movement: they wait in the review queue. */
    suspectedDuplicates: z.number().int(),
    errors: z.array(z.object({ row: z.number().int(), message: z.string() })),
});
export const matchBankMovementRequestSchema = z.object({
    version: versionSchema,
    transactionId: uuidSchema,
});
/** Records the movement as a new income/expense on its account (categorisation). */
export const categorizeBankMovementRequestSchema = z.object({
    version: versionSchema,
    categoryId: uuidSchema,
    counterpartyName: optionalText(200),
    description: optionalText(2000),
});
export const excludeBankMovementRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
export const resolveDuplicateRequestSchema = z.object({
    version: versionSchema,
    isDuplicate: z.boolean(),
    reason: reasonSchema,
});
/** Puts a matched or excluded movement back into the review queue. */
export const reopenBankMovementRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
export const matchCandidateListSchema = z.object({
    items: z.array(financialTransactionSummarySchema),
});
// --- reports ------------------------------------------------------------------------------------
export const pnlFiguresSchema = z.object({
    revenue: z.string(),
    expenses: z.string(),
    /** Revenue − expenses (management operating result). */
    result: z.string(),
});
export const pnlCategoryRowSchema = z.object({
    categoryId: uuidSchema.nullable(),
    code: z.string().nullable(),
    name: z.string(),
    side: z.enum(['REVENUE', 'EXPENSE']),
    month: z.string(),
    ytd: z.string(),
});
export const pnlCurrencyBlockSchema = z.object({
    currency: z.string(),
    month: pnlFiguresSchema,
    ytd: pnlFiguresSchema,
    categories: z.array(pnlCategoryRowSchema),
});
export const consolidatedSchema = z.object({
    currency: z.string(),
    ratesUsed: z.array(appliedRateSchema),
    /** Amounts without a usable historical rate are left out of the totals and listed here. */
    missingRates: z.array(missingRateSchema),
});
export const pnlReportSchema = z.object({
    month: isoMonthSchema,
    ytdFrom: isoDateSchema,
    monthTo: isoDateSchema,
    currencies: z.array(pnlCurrencyBlockSchema),
    consolidated: consolidatedSchema
        .extend({ month: pnlFiguresSchema, ytd: pnlFiguresSchema })
        .nullable(),
    /** Drafts and imported records without a category are not in the figures until recorded. */
    excludedDrafts: z.number().int(),
    generatedAt: isoDateTimeSchema,
});
export const pnlQuerySchema = z.object({ month: isoMonthSchema.optional() });
export const runwaySchema = z.object({
    currency: z.string(),
    windowMonths: z.number().int(),
    windowFrom: isoDateSchema,
    windowTo: isoDateSchema,
    /** Average monthly operating cash outflow net of inflow; negative = cash generative. */
    monthlyNetBurn: z.string(),
    operatingCash: z.string(),
    /** Months of runway (1 decimal); `null` when not burning or without data. */
    runwayMonths: z.string().nullable(),
    status: z.enum(['BURNING', 'NOT_BURNING', 'INSUFFICIENT_DATA']),
});
export const trendPointSchema = z.object({
    month: isoMonthSchema,
    currencies: z.array(pnlFiguresSchema.extend({ currency: z.string() })),
    consolidated: pnlFiguresSchema.nullable(),
});
export const FINANCE_ALERT_KINDS = [
    'UNRECONCILED_MOVEMENTS',
    'SUSPECTED_DUPLICATES',
    'DRAFT_TRANSACTIONS',
    'FOUNDER_PAID_PENDING',
    'MISSING_FX_RATES',
    'BOOK_BANK_DIFFERENCE',
    'NEGATIVE_CASH',
    'STALE_BANK_BALANCE',
];
export const financeAlertKindSchema = z.enum(FINANCE_ALERT_KINDS);
export const financeAlertSchema = z.object({
    kind: financeAlertKindSchema,
    severity: z.enum(['WARNING', 'INFO']),
    message: z.string(),
    count: z.number().int().nullable(),
    link: z.string().nullable(),
});
export const cashCurrencyTotalSchema = z.object({
    currency: z.string(),
    book: z.string(),
    operating: z.string(),
    /** Sum of the latest observed balances of the accounts that have one. */
    observed: z.string().nullable(),
});
export const financeOverviewSchema = z.object({
    asOf: isoDateSchema,
    month: isoMonthSchema,
    settings: financeSettingsSchema,
    thisMonth: z.array(pnlFiguresSchema.extend({
        currency: z.string(),
        openingCash: z.string(),
        closingCash: z.string(),
    })),
    thisMonthConsolidated: pnlFiguresSchema.nullable(),
    cash: z.object({
        accounts: z.array(bankAccountSchema),
        totals: z.array(cashCurrencyTotalSchema),
        consolidated: consolidatedSchema.extend({ book: z.string(), operating: z.string() }).nullable(),
    }),
    runway: z.array(runwaySchema),
    runwayConsolidated: runwaySchema.nullable(),
    trend: z.array(trendPointSchema),
    alerts: z.array(financeAlertSchema),
    generatedAt: isoDateTimeSchema,
});
export const financeOverviewQuerySchema = z.object({ asOf: isoDateSchema.optional() });
//# sourceMappingURL=finance.js.map